import React, { useContext, useCallback } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { APPOINTMENTS_DATE_FORMAT } from '../../Constants';
import { renderAddress } from '../../Utils';
import BSButton from '../../form/BSButton';
import { DataTypeContext } from '../../DataTypes';

const AppointmentCard = (props) => {
    const { t } = useTranslation();
    const { appointment, showActions = true, onCancel, errorMessage, variant = 'default', className = '' } = props;
    const { dataTypes, isDeliveryEnabled } = useContext(DataTypeContext);

    const getBookingPageLink = useCallback(() => {
        if (isDeliveryEnabled && appointment?.vromo_integration) {
            return '/book-delivery';
        }

        return '/book-appointment';
    }, [appointment, isDeliveryEnabled]);

    const getModifyPath = useCallback(() => '/modify-appointment', []);

    let colWidth = 12;
    if (
        dataTypes?.appointmentOptions?.enableAppointmentModification &&
        dataTypes?.appointmentOptions?.enableAppointmentCancellation
    ) {
        colWidth = 6;
    }

    if (variant === 'upcoming') {
        const appointmentDate = moment(appointment.datetime);

        return (
            <Card className={`mb-3 border shadow-sm ${className}`}>
                <Card.Body className="p-3 p-md-4">
                    <Card.Title className="mb-3">
                        {appointment?.location?.name}
                    </Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">
                        {renderAddress(appointment?.location?.address)}
                    </Card.Subtitle>
                    {appointment?.program?.name && (
                        <Card.Text className="text-muted mb-3">
                            {appointment.program.name}
                        </Card.Text>
                    )}

                    <hr className="my-3" />

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="text-muted">Date:</span>
                            <span className="font-weight-bold text-right">
                                {appointmentDate.format('dddd, MMMM D, YYYY')}
                            </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-start">
                            <span className="text-muted">Time:</span>
                            <span className="font-weight-bold text-right">{appointmentDate.format('h:mm A')}</span>
                        </div>
                    </div>

                    {showActions && (
                        <Row>
                            {dataTypes?.appointmentOptions?.enableAppointmentModification && (
                                <Col xs={colWidth} className="mb-2 mb-md-0">
                                    <BSButton
                                        as={Link}
                                        to={{ pathname: getModifyPath(), state: { existingAppointment: appointment } }}
                                        variant="outline-secondary"
                                        className="w-100"
                                    >
                                        {t('core.action.modify')}
                                    </BSButton>
                                </Col>
                            )}
                            {dataTypes?.appointmentOptions?.enableAppointmentCancellation && (
                                <Col xs={colWidth} className="mb-2 mb-md-0">
                                    <BSButton
                                        variant="outline-danger"
                                        onClick={onCancel}
                                        className="w-100"
                                    >
                                        {t('core.action.cancel')}
                                    </BSButton>
                                </Col>
                            )}
                        </Row>
                    )}

                    {errorMessage && (
                        <Card.Text className="mt-3 mb-0 text-muted">
                            {errorMessage}
                        </Card.Text>
                    )}
                </Card.Body>
            </Card>
        );
    }

    if (variant === 'summary') {
        const appointmentDate = moment(appointment?.datetime);
        const locationValue = appointment?.location?.name
            ? `${appointment.location.name} (${renderAddress(appointment?.location?.address)})`
            : renderAddress(appointment?.location?.address);

        return (
            <Card className={`mb-4 border ${className}`}>
                <Card.Body className="p-3 p-md-4">
                    <Card.Title className="h4 mb-3">Current Appointment</Card.Title>
                    <Row className="mb-2 align-items-start">
                        <Col xs={4} className="text-muted">Location:</Col>
                        <Col xs={8} className="text-right">{locationValue}</Col>
                    </Row>
                    <Row className="mb-2 align-items-start">
                        <Col xs={4} className="text-muted">Date:</Col>
                        <Col xs={8} className="text-right">{appointmentDate.format('dddd, MMMM D, YYYY')}</Col>
                    </Row>
                    <Row className="align-items-start">
                        <Col xs={4} className="text-muted">Time:</Col>
                        <Col xs={8} className="text-right">{appointmentDate.format('h:mm A')}</Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className={`mb-3 text-center ${className}`}>
            <Card.Body>
                <Card.Title>{moment(appointment.datetime).format(APPOINTMENTS_DATE_FORMAT)}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{appointment.program.name}</Card.Subtitle>
                <Card.Subtitle className="mb-2 text-muted">{appointment.location.name}</Card.Subtitle>
                <Card.Text>{renderAddress(appointment.location.address)}</Card.Text>
                {showActions && (
                    <Row>
                        {dataTypes?.appointmentOptions?.enableAppointmentModification && <Col xs={colWidth}>
                            <BSButton
                                as={Link}
                                to={{ pathname: getBookingPageLink(), state: { existingAppointment: appointment } }}
                                icon="fa-pencil-square-o"
                                className="text-white"
                                variant="warning"
                            >
                                {t('core.action.modify')}
                            </BSButton>
                        </Col>}
                        {dataTypes?.appointmentOptions?.enableAppointmentCancellation && <Col xs={colWidth}>
                            <Card.Link as={BSButton} icon="fa-times" variant="danger" onClick={onCancel}>
                                {t('core.action.cancel')}
                            </Card.Link>
                        </Col>}
                    </Row>
                )}
                {errorMessage && (
                    <Card.Text className="mt-2">
                        <span className="text-warning">{errorMessage}</span>
                    </Card.Text>
                )}
            </Card.Body>
        </Card>
    );
};

export default AppointmentCard;
